/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import __ from "web/Layout/Translations";

import { IApiRequestMethods } from "web/api/apiRequestTypes";

import CANCELLATION_POLICY from "web/constants/cancellationPolicy";
import restUrls from "web/constants/restUrls";

import { IDomesticTourismHotelRatesOffer, IRoom } from "web/types/Tourism";
import { Nullable } from "web/types/Utils";

import { request } from "web/api";

export const useTourismCheckoutInfo = (
  offer: IDomesticTourismHotelRatesOffer,
  minPercentage: Nullable<number>
) => {
  const warningCopy = __(
    "Brak płatności w podanych kwotach i terminach będzie skutkował anulacją kosztową rezerwacji zgodnie z warunkami podanymi powyżej."
  );

  return useMemo(() => {
    const {
      cnxs: cnxsBeforeMinPrice,
      rooms,
      total_price,
      source,
      cnxs_min_price: cnxsMinPrice,
    } = offer || {};

    const cnxs = cnxsMinPrice || cnxsBeforeMinPrice;

    if (!cnxs || !rooms || !total_price || !minPercentage) {
      return null;
    }
    const isGDS = source !== "MYBENEFIT";
    const cancellationPolicy = rooms[0]?.cnx && rooms[0].cnx;
    const isRefundable =
      cancellationPolicy !== CANCELLATION_POLICY.NON_REFUNDABLE ||
      (isGDS &&
        (cancellationPolicy === CANCELLATION_POLICY.UNKNOWN ||
          !cancellationPolicy));

    if (!isRefundable) {
      return [
        `${__("Wymagamy od Ciebie płatności w kwocie")} ${total_price} ${__(
          "zł"
        )}.`,
      ];
    }

    const hasDates = Boolean(cnxs[0]?.from && cnxs[0].to);

    if (hasDates) {
      return [
        ...cnxs.reduce(
          (acc, { from, value }) => {
            acc.push(
              `${__("Do dnia")} ${from}, godz. 21:59 – ${value} ${__("zł")}`
            );

            return acc;
          },
          [
            __("Wymagamy od Ciebie płatności za pobyt w poniższych terminach:"),
            warningCopy,
          ]
        ),
      ];
    }

    const minPrice = Math.round(+total_price * minPercentage) / 100;

    return [
      `${__("Wymagamy od Ciebie płatności w kwocie")} ${minPrice} ${__("zł")}`,
    ];
  }, [offer, minPercentage]);
};

export const useTourismCheckoutInfoWithRedux = () => {
  const { offer, minPercentage } = useSelector(
    ({ tourismQuoteDomestic: { percentage, quote } }) => ({
      minPercentage: percentage,
      offer: quote?.offer,
    })
  );

  const checkoutInfo = useTourismCheckoutInfo(offer!, minPercentage || 100);

  return checkoutInfo;
};

export const useTourismCheckoutInfoWithMinPriceFetch = (
  offer: IDomesticTourismHotelRatesOffer & { room: IRoom[] }
) => {
  const [minPercentage, setMinPercentage] = useState<Nullable<number>>(null);

  // @TODO refactor to rtk when all RTK changes will be merged to master
  useEffect(() => {
    let isMounted = true;
    const fetchMinPercentage = async () => {
      const response = await request(restUrls.minPriceAnixe, {
        method: IApiRequestMethods.POST,
        body: JSON.stringify({
          supplier_code: offer.source,
          cnx_type: offer.room[0].cnx_type,
          total_price: offer.total_price,
          cnxs: offer.cnxs,
        }),
      });
      if (isMounted) {
        setMinPercentage(response as number);
      }
    };
    fetchMinPercentage();
    return () => {
      isMounted = false;
    };
  }, []);

  const checkoutInfo = useTourismCheckoutInfo(
    { ...offer, rooms: offer.room },
    minPercentage || 100
  );

  return checkoutInfo;
};
